/**
 * UranIA PA — Banca Dati admin (``/admin/corpus`` — era
 * ``/admin/banca-dati``, fusione A.4.5, "Riordino della mappa delle
 * rotte" §4: "banca dati editoriale: conteggi, ricerca, metadata"). La
 * vista tecnica della pipeline RAG che occupava questo path prima si è
 * spostata su ``/admin/corpus/ingestione`` (era ``/admin/corpus``, vedi
 * ``admin/corpus/ingestione/index.tsx``).
 *
 * Console editoriale del corpus documentale, distinta da
 * ``/admin/corpus/ingestione`` (vista tecnica della pipeline RAG). Permette di:
 *
 *   - vedere conteggi per categoria (stat cards = tab);
 *   - cercare i documenti per titolo/tag/anno/tipo atto;
 *   - aggiungere documenti con metadata editoriali (titolo, categoria,
 *     tipo atto, anno, fonte, regione, tag) — l'ingestion RAG parte
 *     contestualmente (saga Arq: chunk + embed e5-large + index
 *     Qdrant/OpenSearch);
 *   - rimuovere un documento dalla Banca Dati (il chunk index resta:
 *     pulizia integrata in migration Fase 4).
 *
 * RBAC: ``platform_admin`` / ``tenant_admin``. Lato server l'autorizzazione
 * e' enforced; qui solo UX guard.
 *
 * Categorie (allineate alle 4 tab della Banca Dati storica):
 *   - ``eur_lex``           — Regolamenti UE, Direttive, Decisioni
 *   - ``ministeri``         — Decreti, circolari, linee guida nazionali
 *   - ``regioni``           — DGR, Manuale Attuazione, SORI, GURS
 *   - ``corpus_scientifico``— Paper, studi, dataset citabili
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';

import {
  CorpusApiError,
  deleteCorpusDocument,
  enqueueCorpusIngest,
  getCorpusJobStatus,
  getCorpusStats,
  listCorpusDocuments,
  listCorpusTags,
} from '../../../lib/admin/corpus-api.ts';
import type {
  CorpusCategoria,
  CorpusDocumentDto,
  CorpusDataClassification,
  CorpusIngestEnqueueDto,
  CorpusJobStatusDto,
  CorpusStatsDto,
  CorpusTenantScope,
  ListCorpusDocumentsDto,
} from '../../../lib/admin/corpus-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsAnyAdmin } from '../../../lib/auth/rbac.ts';
import { Route as AmministrazioneSegmentRoute } from '../../_amministrazione.tsx';

const DOC_ID_PATTERN = /^[a-z0-9_\-\.]+$/u;
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

const CATEGORIES: readonly CorpusCategoria[] = [
  'eur_lex',
  'ministeri',
  'regioni',
  'corpus_scientifico',
];

const TIPI_ATTO_SUGGESTIONS: readonly string[] = [
  'Regolamento',
  'Direttiva',
  'Decisione',
  'Comunicazione',
  'Raccomandazione',
  'Decreto',
  'DGR',
  'Circolare',
  'Linee guida',
  'Manuale',
  'Paper scientifico',
  'Studio',
];

function CategoriaBadge(props: { readonly categoria: CorpusCategoria }): JSX.Element {
  const { t } = useTranslation();
  const colors: Record<CorpusCategoria, { bg: string; fg: string }> = {
    eur_lex: { bg: '#dbe4ff', fg: '#1a237e' },
    ministeri: { bg: '#f3e5f5', fg: '#4a148c' },
    regioni: { bg: '#ffe0b2', fg: '#a04400' },
    corpus_scientifico: { bg: '#d1f1e3', fg: '#0f5132' },
  };
  const c = colors[props.categoria];
  return (
    <span
      className="badge"
      style={{ backgroundColor: c.bg, color: c.fg, fontSize: '0.65rem' }}
    >
      {t(`admin.banca_dati.categorie.${props.categoria}`)}
    </span>
  );
}

function IngestionStatusBadge(props: {
  readonly status: CorpusDocumentDto['ingestion_status'];
}): JSX.Element {
  const { t } = useTranslation();
  const map: Record<
    CorpusDocumentDto['ingestion_status'],
    { bg: string; fg: string }
  > = {
    queued: { bg: '#fff3cd', fg: '#664d03' },
    in_progress: { bg: '#cfe2ff', fg: '#084298' },
    complete: { bg: '#d1e7dd', fg: '#0f5132' },
    failed: { bg: '#f8d7da', fg: '#842029' },
    unknown: { bg: '#e9ecef', fg: '#495057' },
  };
  const c = map[props.status];
  return (
    <span
      className="badge"
      style={{ backgroundColor: c.bg, color: c.fg, fontSize: '0.6rem' }}
    >
      {t(`admin.banca_dati.ingestion_status.${props.status}`)}
    </span>
  );
}

interface UploadFormProps {
  readonly defaultCategoria: CorpusCategoria | null;
  readonly onClose: () => void;
}

function UploadForm(props: UploadFormProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileId = useId();
  const docIdId = useId();
  const titoloId = useId();
  const categoriaId = useId();
  const tipoAttoId = useId();
  const annoId = useId();
  const fonteId = useId();
  const tagsId = useId();
  const descrizioneId = useId();
  const scopeId = useId();
  const classId = useId();
  const regioneId = useId();

  const [file, setFile] = useState<File | null>(null);
  const [docId, setDocId] = useState('');
  const [titolo, setTitolo] = useState('');
  const [categoria, setCategoria] = useState<CorpusCategoria>(
    props.defaultCategoria ?? 'eur_lex',
  );
  const [tipoAtto, setTipoAtto] = useState('Regolamento');
  const [anno, setAnno] = useState<number | ''>(new Date().getFullYear());
  const [fonte, setFonte] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [tenantScope, setTenantScope] = useState<CorpusTenantScope>('core');
  const [dataClassification, setDataClassification] =
    useState<CorpusDataClassification>('internal');
  const [regione, setRegione] = useState('');

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [enqueue, setEnqueue] = useState<CorpusIngestEnqueueDto | null>(null);
  const [jobStatus, setJobStatus] = useState<CorpusJobStatusDto | null>(null);

  const tagsArr = useMemo(
    () =>
      tagsRaw
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    [tagsRaw],
  );

  const upload = useMutation<CorpusIngestEnqueueDto, CorpusApiError>({
    mutationFn: async () => {
      if (file === null) {
        throw new CorpusApiError(0, t('admin.banca_dati.form.errors.file_required'), null);
      }
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        throw new CorpusApiError(0, t('admin.banca_dati.form.errors.mime_invalid'), null);
      }
      if (docId.length < 3 || !DOC_ID_PATTERN.test(docId)) {
        throw new CorpusApiError(0, t('admin.banca_dati.form.errors.doc_id_invalid'), null);
      }
      if (titolo.trim().length === 0) {
        throw new CorpusApiError(0, t('admin.banca_dati.form.errors.titolo_required'), null);
      }
      return await enqueueCorpusIngest({
        file,
        docId,
        titolo: titolo.trim(),
        categoria,
        tipoAtto,
        descrizione,
        fonte,
        anno: typeof anno === 'number' ? anno : null,
        tags: tagsArr,
        tenantScope,
        dataClassification,
        ...(categoria === 'regioni' && regione.length > 0 ? { regione } : {}),
      });
    },
    onSuccess: async (dto) => {
      setEnqueue(dto);
      setSubmitError(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'banca-dati'] });
    },
    onError: (err) => {
      setSubmitError(err.detail ?? err.message);
    },
  });

  useEffect(() => {
    if (enqueue === null) return undefined;
    let cancelled = false;
    let timer: number | undefined;
    const poll = async (): Promise<void> => {
      try {
        const next = await getCorpusJobStatus(enqueue.job_id);
        if (cancelled) return;
        setJobStatus(next);
        if (
          next.status === 'queued' ||
          next.status === 'deferred' ||
          next.status === 'in_progress'
        ) {
          timer = window.setTimeout(poll, 3000);
        } else {
          void qc.invalidateQueries({ queryKey: ['admin', 'banca-dati'] });
        }
      } catch {
        // best-effort
      }
    };
    void poll();
    return (): void => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [enqueue, qc]);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    upload.mutate();
  };

  return (
    <form onSubmit={onSubmit} data-testid="admin-banca-dati-form">
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label small text-muted mb-1" htmlFor={titoloId}>
            {t('admin.banca_dati.form.titolo')}
          </label>
          <input
            id={titoloId}
            type="text"
            className="form-control form-control-sm"
            value={titolo}
            onChange={(e): void => setTitolo(e.target.value)}
            required
            maxLength={500}
            data-testid="admin-banca-dati-form-titolo"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1" htmlFor={categoriaId}>
            {t('admin.banca_dati.form.categoria')}
          </label>
          <select
            id={categoriaId}
            className="form-select form-select-sm"
            value={categoria}
            onChange={(e): void => setCategoria(e.target.value as CorpusCategoria)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`admin.banca_dati.categorie.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1" htmlFor={annoId}>
            {t('admin.banca_dati.form.anno')}
          </label>
          <input
            id={annoId}
            type="number"
            min={1900}
            max={2100}
            className="form-control form-control-sm"
            value={anno}
            onChange={(e): void => {
              const v = e.target.value;
              setAnno(v === '' ? '' : Number(v));
            }}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label small text-muted mb-1" htmlFor={tipoAttoId}>
            {t('admin.banca_dati.form.tipo_atto')}
          </label>
          <input
            id={tipoAttoId}
            type="text"
            className="form-control form-control-sm"
            value={tipoAtto}
            onChange={(e): void => setTipoAtto(e.target.value)}
            list={`${tipoAttoId}-suggest`}
            maxLength={128}
          />
          <datalist id={`${tipoAttoId}-suggest`}>
            {TIPI_ATTO_SUGGESTIONS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
        <div className="col-md-4">
          <label className="form-label small text-muted mb-1" htmlFor={fonteId}>
            {t('admin.banca_dati.form.fonte')}
          </label>
          <input
            id={fonteId}
            type="text"
            className="form-control form-control-sm"
            value={fonte}
            onChange={(e): void => setFonte(e.target.value)}
            placeholder={t('admin.banca_dati.form.fonte_placeholder')}
            maxLength={256}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small text-muted mb-1" htmlFor={docIdId}>
            {t('admin.banca_dati.form.doc_id')}
          </label>
          <input
            id={docIdId}
            type="text"
            className="form-control form-control-sm font-monospace"
            value={docId}
            onChange={(e): void => setDocId(e.target.value)}
            placeholder="es. eur-lex-651-2014-gber"
            required
            pattern={DOC_ID_PATTERN.source}
          />
          <small className="form-text text-muted">{t('admin.banca_dati.form.doc_id_hint')}</small>
        </div>

        {categoria === 'regioni' && (
          <div className="col-md-12">
            <label className="form-label small text-muted mb-1" htmlFor={regioneId}>
              {t('admin.banca_dati.form.regione')}
            </label>
            <input
              id={regioneId}
              type="text"
              className="form-control form-control-sm"
              value={regione}
              onChange={(e): void => setRegione(e.target.value)}
              placeholder="Sicilia"
              maxLength={128}
            />
          </div>
        )}

        <div className="col-md-12">
          <label className="form-label small text-muted mb-1" htmlFor={tagsId}>
            {t('admin.banca_dati.form.tags')}
          </label>
          <input
            id={tagsId}
            type="text"
            className="form-control form-control-sm"
            value={tagsRaw}
            onChange={(e): void => setTagsRaw(e.target.value)}
            placeholder={t('admin.banca_dati.form.tags_placeholder')}
          />
          {tagsArr.length > 0 && (
            <div className="mt-1">
              {tagsArr.map((tag) => (
                <span
                  key={tag}
                  className="badge bg-light text-dark border me-1"
                  style={{ fontSize: '0.65rem' }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="col-md-12">
          <label className="form-label small text-muted mb-1" htmlFor={descrizioneId}>
            {t('admin.banca_dati.form.descrizione')}
          </label>
          <textarea
            id={descrizioneId}
            className="form-control form-control-sm"
            rows={2}
            value={descrizione}
            onChange={(e): void => setDescrizione(e.target.value)}
            maxLength={2000}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label small text-muted mb-1" htmlFor={fileId}>
            {t('admin.banca_dati.form.file')}
          </label>
          <input
            id={fileId}
            type="file"
            className="form-control form-control-sm"
            accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            onChange={(e): void => setFile(e.target.files?.[0] ?? null)}
            required
            data-testid="admin-banca-dati-form-file"
          />
          <small className="form-text text-muted">{t('admin.banca_dati.form.file_hint')}</small>
        </div>
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1" htmlFor={scopeId}>
            {t('admin.banca_dati.form.scope')}
          </label>
          <select
            id={scopeId}
            className="form-select form-select-sm"
            value={tenantScope}
            onChange={(e): void => setTenantScope(e.target.value as CorpusTenantScope)}
          >
            <option value="core">{t('admin.banca_dati.form.scope_core')}</option>
            <option value="tenant">{t('admin.banca_dati.form.scope_tenant')}</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label small text-muted mb-1" htmlFor={classId}>
            {t('admin.banca_dati.form.data_classification')}
          </label>
          <select
            id={classId}
            className="form-select form-select-sm"
            value={dataClassification}
            onChange={(e): void => setDataClassification(e.target.value as CorpusDataClassification)}
          >
            <option value="public">public</option>
            <option value="internal">internal</option>
            <option value="restricted">restricted</option>
            <option value="secret">secret</option>
          </select>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top gap-2">
        <div className="flex-grow-1">
          {submitError !== null && (
            <div className="text-danger small" role="alert">
              {submitError}
            </div>
          )}
          {enqueue !== null && (
            <div className="small text-muted">
              {t('admin.banca_dati.form.enqueued', { jobId: enqueue.job_id })}{' '}
              {jobStatus !== null && (
                <span>
                  · {t(`admin.banca_dati.ingestion_status.${jobStatus.status === 'not_found' ? 'unknown' : jobStatus.status}`)}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={props.onClose}
        >
          {t('admin.banca_dati.form.close')}
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={upload.isPending}
          data-testid="admin-banca-dati-form-submit"
        >
          {upload.isPending
            ? t('admin.banca_dati.form.submitting')
            : t('admin.banca_dati.form.submit')}
        </button>
      </div>
    </form>
  );
}

function StatCard(props: {
  readonly value: number;
  readonly label: string;
  readonly color: string;
  readonly active: boolean;
  readonly onClick: () => void;
  readonly testId: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className="card border-0 shadow-sm text-start w-100"
      onClick={props.onClick}
      data-testid={props.testId}
      style={{
        borderLeft: `4px solid ${props.color}`,
        backgroundColor: props.active ? '#f8f9ff' : '#fff',
        cursor: 'pointer',
      }}
    >
      <div className="card-body py-3 px-3">
        <div className="h3 mb-0" style={{ color: props.color }}>
          {props.value}
        </div>
        <small className="text-muted">{props.label}</small>
      </div>
    </button>
  );
}

export function AdminBancaDatiPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isAdmin = principalIsAnyAdmin(principal);
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState<CorpusCategoria | 'all'>('all');
  const [search, setSearch] = useState('');
  const [annoFilter, setAnnoFilter] = useState<number | ''>('');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [showUpload, setShowUpload] = useState(false);

  const stats = useQuery<CorpusStatsDto, CorpusApiError>({
    queryKey: ['admin', 'banca-dati', 'stats'],
    queryFn: getCorpusStats,
    enabled: isAuthenticated && isAdmin,
    staleTime: 10_000,
  });

  const tagsQuery = useQuery<{ tags: readonly string[] }, CorpusApiError>({
    queryKey: ['admin', 'banca-dati', 'tags'],
    queryFn: listCorpusTags,
    enabled: isAuthenticated && isAdmin,
    staleTime: 30_000,
  });

  const docs = useQuery<ListCorpusDocumentsDto, CorpusApiError>({
    queryKey: ['admin', 'banca-dati', 'list', activeTab, search, annoFilter, tagFilter],
    queryFn: () =>
      listCorpusDocuments({
        categoria: activeTab === 'all' ? null : activeTab,
        q: search.trim(),
        anno: typeof annoFilter === 'number' ? annoFilter : null,
        tag: tagFilter.length > 0 ? tagFilter : null,
      }),
    enabled: isAuthenticated && isAdmin,
    staleTime: 5_000,
  });

  const deleteMutation = useMutation<void, CorpusApiError, string>({
    mutationFn: deleteCorpusDocument,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'banca-dati'] });
    },
  });

  if (!isAuthenticated) {
    return (
      <section data-testid="admin-banca-dati-unauthenticated">
        <h1 className="h3 mb-3">{t('admin.banca_dati.title')}</h1>
        <div className="alert alert-info">
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link to="/accedi" className="btn btn-primary">
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }
  if (!isAdmin) {
    return (
      <section data-testid="admin-banca-dati-forbidden">
        <h1 className="h3 mb-3">{t('admin.banca_dati.title')}</h1>
        <div className="alert alert-warning">
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  const items = docs.data?.items ?? [];

  return (
    <section className="urania-admin-banca-dati" data-testid="admin-banca-dati-page">
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          ← {t('admin.console.title')}
        </Link>
      </nav>

      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div style={{ maxWidth: 720 }}>
            <span
              className="badge mb-2"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
              }}
            >
              ADMIN · BANCA DATI
            </span>
            <h1 className="h3 mb-2">{t('admin.banca_dati.title')}</h1>
            <p className="mb-0 opacity-90 small">{t('admin.banca_dati.subtitle')}</p>
          </div>
          <button
            type="button"
            className="btn btn-light"
            onClick={(): void => setShowUpload((v) => !v)}
            data-testid="admin-banca-dati-new"
          >
            + {t('admin.banca_dati.actions.new')}
          </button>
        </div>
      </div>

      {stats.isSuccess && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-lg">
            <StatCard
              value={stats.data.total}
              label={t('admin.banca_dati.stats.total')}
              color="#003d82"
              active={activeTab === 'all'}
              onClick={(): void => setActiveTab('all')}
              testId="admin-banca-dati-stat-total"
            />
          </div>
          <div className="col-6 col-lg">
            <StatCard
              value={stats.data.eur_lex}
              label={t('admin.banca_dati.categorie.eur_lex')}
              color="#1a237e"
              active={activeTab === 'eur_lex'}
              onClick={(): void => setActiveTab('eur_lex')}
              testId="admin-banca-dati-stat-eur-lex"
            />
          </div>
          <div className="col-6 col-lg">
            <StatCard
              value={stats.data.ministeri}
              label={t('admin.banca_dati.categorie.ministeri')}
              color="#4a148c"
              active={activeTab === 'ministeri'}
              onClick={(): void => setActiveTab('ministeri')}
              testId="admin-banca-dati-stat-ministeri"
            />
          </div>
          <div className="col-6 col-lg">
            <StatCard
              value={stats.data.regioni}
              label={t('admin.banca_dati.categorie.regioni')}
              color="#a04400"
              active={activeTab === 'regioni'}
              onClick={(): void => setActiveTab('regioni')}
              testId="admin-banca-dati-stat-regioni"
            />
          </div>
          <div className="col-6 col-lg">
            <StatCard
              value={stats.data.corpus_scientifico}
              label={t('admin.banca_dati.categorie.corpus_scientifico')}
              color="#0f5132"
              active={activeTab === 'corpus_scientifico'}
              onClick={(): void => setActiveTab('corpus_scientifico')}
              testId="admin-banca-dati-stat-corpus"
            />
          </div>
        </div>
      )}

      {showUpload && (
        <div className="card shadow-sm border-0 mb-3" data-testid="admin-banca-dati-upload">
          <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {t('admin.banca_dati.form.heading')}
            </span>
          </div>
          <div className="card-body">
            <UploadForm
              defaultCategoria={activeTab === 'all' ? null : activeTab}
              onClose={(): void => setShowUpload(false)}
            />
          </div>
        </div>
      )}

      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body py-2 px-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label small text-muted mb-1">
                {t('admin.banca_dati.filters.search_label')}
              </label>
              <input
                type="search"
                className="form-control form-control-sm"
                value={search}
                onChange={(e): void => setSearch(e.target.value)}
                placeholder={t('admin.banca_dati.filters.search_placeholder')}
                data-testid="admin-banca-dati-filter-search"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1">
                {t('admin.banca_dati.filters.anno_label')}
              </label>
              <input
                type="number"
                min={1900}
                max={2100}
                className="form-control form-control-sm"
                value={annoFilter}
                onChange={(e): void => {
                  const v = e.target.value;
                  setAnnoFilter(v === '' ? '' : Number(v));
                }}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small text-muted mb-1">
                {t('admin.banca_dati.filters.tag_label')}
              </label>
              <select
                className="form-select form-select-sm"
                value={tagFilter}
                onChange={(e): void => setTagFilter(e.target.value)}
              >
                <option value="">{t('admin.banca_dati.filters.tag_all')}</option>
                {(tagsQuery.data?.tags ?? []).map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-1 d-flex justify-content-end">
              {(search.length > 0 || annoFilter !== '' || tagFilter.length > 0) && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={(): void => {
                    setSearch('');
                    setAnnoFilter('');
                    setTagFilter('');
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {docs.isLoading && (
        <p role="status" aria-live="polite">
          {t('admin.banca_dati.loading')}
        </p>
      )}
      {docs.isError && (
        <div className="alert alert-danger" role="alert">
          <strong>{t('admin.banca_dati.errors.load_failed')}</strong>
          {docs.error.detail !== null && <p className="mb-0">{docs.error.detail}</p>}
        </div>
      )}

      {docs.isSuccess && (
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-bottom py-2 px-3">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {t('admin.banca_dati.list_heading', { count: docs.data.total })}
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0" data-testid="admin-banca-dati-table">
              <thead>
                <tr>
                  <th>{t('admin.banca_dati.columns.titolo')}</th>
                  <th>{t('admin.banca_dati.columns.categoria')}</th>
                  <th>{t('admin.banca_dati.columns.tipo_atto')}</th>
                  <th>{t('admin.banca_dati.columns.anno')}</th>
                  <th>{t('admin.banca_dati.columns.fonte')}</th>
                  <th>{t('admin.banca_dati.columns.tags')}</th>
                  <th>{t('admin.banca_dati.columns.ingestion_status')}</th>
                  <th className="text-end">
                    <span className="visually-hidden">
                      {t('admin.banca_dati.columns.actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted small fst-italic py-4">
                      {t('admin.banca_dati.empty')}
                    </td>
                  </tr>
                )}
                {items.map((d) => (
                  <tr key={d.doc_id} data-testid={`admin-banca-dati-row-${d.doc_id}`}>
                    <td>
                      <strong>{d.titolo}</strong>
                      <br />
                      <code className="small text-muted">{d.doc_id}</code>
                      {d.descrizione.length > 0 && (
                        <p className="small text-muted mb-0 mt-1">{d.descrizione}</p>
                      )}
                    </td>
                    <td>
                      <CategoriaBadge categoria={d.categoria} />
                      {d.regione !== null && d.regione.length > 0 && (
                        <span
                          className="badge ms-1"
                          style={{
                            backgroundColor: '#fff3cd',
                            color: '#664d03',
                            fontSize: '0.6rem',
                          }}
                        >
                          {d.regione}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="small">{d.tipo_atto}</span>
                    </td>
                    <td>{d.anno ?? '—'}</td>
                    <td>
                      <small className="text-muted">{d.fonte || '—'}</small>
                    </td>
                    <td>
                      {d.tags.length === 0 ? (
                        <span className="text-muted small">—</span>
                      ) : (
                        d.tags.map((tag) => (
                          <span
                            key={tag}
                            className="badge bg-light text-dark border me-1 mb-1"
                            style={{ fontSize: '0.6rem' }}
                          >
                            {tag}
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      <IngestionStatusBadge status={d.ingestion_status} />
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={(): void => {
                          if (
                            window.confirm(
                              t('admin.banca_dati.actions.delete_confirm', {
                                titolo: d.titolo,
                              }),
                            )
                          ) {
                            deleteMutation.mutate(d.doc_id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        data-testid={`admin-banca-dati-delete-${d.doc_id}`}
                      >
                        {t('admin.banca_dati.actions.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => AmministrazioneSegmentRoute,
  path: '/admin/corpus',
  component: AdminBancaDatiPage,
});
